import { createI18n } from 'vue-i18n'
import { computed } from 'vue'
import { createTLI18n as createComponentsTLI18n } from 'widget-components'
import { LANG } from '@/lib/supported-langs'

const locales: Record<string, LANG> = {
    'de-at':  LANG.DE, // German     -  Austria
    'de-ch':  LANG.DE, // German     -  Switzerland
    'de-de':  LANG.DE, // German     -  Germany
    'de-li':  LANG.DE, // German     -  Liechtenstein
    'de-lu':  LANG.DE, // German     -  Luxembourg
    de:     LANG.DE, // German
    'en-gb':  LANG.EN, // English    -  United  Kingdom
    'en-us':  LANG.EN, // English    -  United  States
    en:     LANG.EN, // English
    'es-ar':  LANG.ES, // Spanish    -  Argentina
    'es-cl':  LANG.ES, // Spanish    -  Chile
    'es-co':  LANG.ES, // Spanish    -  Colombia
    'es-ec':  LANG.ES, // Spanish    -  Ecuador
    'es-es':  LANG.ES, // Spanish    -  Spain
    'es-mx':  LANG.ES, // Spanish    -  Mexico
    'es-pe':  LANG.ES, // Spanish    -  Peru
    'es-pr':  LANG.ES, // Spanish    -  Puerto  Rico
    'es-uy':  LANG.ES, // Spanish    -  Uruguay
    'es-ve':  LANG.ES, // Spanish    -  Venezuela
    es:     LANG.ES, // Spanish
    fi:     LANG.FI, // Finnish
    'fr-be':  LANG.FR, // French     -  Belgium
    'fr-ca':  LANG.FR, // French     -  Canada
    'fr-ch':  LANG.FR, // French     -  Switzerland
    'fr-fr':  LANG.FR, // French     -  France
    'fr-lu':  LANG.FR, // French     -  Luxembourg
    'fr-mc':  LANG.FR, // French     -  Monaco
    fr:     LANG.FR, // French
    'it-ch':  LANG.IT, // Italian    -  Switzerland
    'it-it':  LANG.IT, // Italian    -  Italy
    it:     LANG.IT, // Italian
    lt:     LANG.LT, // Lithuanian
    'nl-be':  LANG.NL, // Dutch      -  Belgium (Flemish)
    'nl-nl':  LANG.NL, // Dutch      -  Netherlands
    nl:     LANG.NL, // Dutch
    'pl-pl':  LANG.PL, // Polish     -  Poland
    pl:     LANG.PL, // Polish
    'pt-ao':  LANG.PT, // Portuguese -  Angola
    'pt-br':  LANG.PT, // Portuguese -  Brazil
    'pt-cv':  LANG.PT, // Portuguese -  Cape    Verde
    'pt-mz':  LANG.PT, // Portuguese -  Mozambique
    'pt-pt':  LANG.PT, // Portuguese -  Portugal
    pt:     LANG.PT, // Portuguese
    ua:     LANG.UK, // Ukrainian
    'uk-ua':  LANG.UK, // Ukrainian  -  Ukraine
    uk:     LANG.UK, // Ukrainian  -  (alternative code)
    fallback: LANG.EN, // Fallback language
}

export class TLI18n {
    i18n

    constructor(lang: LANG) {
        this.i18n = createI18n({
            legacy: false,
            locale: lang,
            fallbackLocale: 'en',
        })
    }
}

let I18N_INSTANCE: null | TLI18n = null
let I18N_INSTANCE_WIDGET_INSTANCE: null | TLI18n = null

export async function createTLI18n(lang: string) {
    if (I18N_INSTANCE) {
        // eslint-disable-next-line no-console
        console.error('i18n service already created')
        return
    }

    const pickedLocale: LANG = locales[lang.toLowerCase()] || locales.fallback

    I18N_INSTANCE = new TLI18n(pickedLocale)
    I18N_INSTANCE_WIDGET_INSTANCE = createComponentsTLI18n(pickedLocale) as TLI18n

    await loadTexts(pickedLocale)

    return I18N_INSTANCE.i18n
}

const loadTexts = async (lang: string) => {
    if (I18N_INSTANCE) {
        const localTexts = await import(`../../texts/${lang}.json`)
        I18N_INSTANCE.i18n.global.setLocaleMessage(lang, localTexts.default)
        return
    }
}

const changeLang = async (value?: string) => {
    if (I18N_INSTANCE && I18N_INSTANCE_WIDGET_INSTANCE && value) {
        const lang = locales[value.toLowerCase()]
        if (lang) {
            I18N_INSTANCE.i18n.global.locale.value = lang
            I18N_INSTANCE_WIDGET_INSTANCE.i18n.global.locale.value = lang
            await loadTexts(lang)
        } else {
            // eslint-disable-next-line no-console
            console.error(`${lang} lang not available, please use one of these: ${Object.values(LANG).join(', ')}`)
        }
    }
}

const getCurrentLocale = computed(() => {
    if (I18N_INSTANCE) {
        return locales[I18N_INSTANCE.i18n.global?.locale?.value?.toLowerCase()] || 'en'
    }

    return LANG.EN
})

export function useI18n() {
    if (!I18N_INSTANCE) {
        throw 'i18n haven\'t been created'
    }

    return Object.assign({ changeLang, getCurrentLocale }, I18N_INSTANCE.i18n.global)
}
